import React from 'react'
// libs
import { useMsal } from '@azure/msal-react'
import { useRouter } from 'hooks/useRouter'
import { useSnackbar } from 'notistack'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { setAccessToken } from 'lib/auth'
import { loginRequest, silentRequest } from 'providers/MicrosoftProvider/config'
// graphql
import { MICROSOFT_GET_COMPANIES, MICROSOFT_SIGN_IN_MUTATION } from 'graphql/auth/mutations'
// hooks
import { useShowModal } from 'hooks'
// store
import { setCurrentUser } from 'store/reducers/currentUser'
// constants
import { Modals } from 'constants/modals'
// types
import type { AuthenticationResult, RedirectRequest } from '@azure/msal-browser'
import { EventMessage, EventType, InteractionRequiredAuthError } from '@azure/msal-browser'
import { IAuthMicrosoftTokenGetAvailableCompaniesResponse, IAuthUserWithCredentialsResponse } from 'types/graphql'
// utils
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'
import { getUserMessage, normalizeError } from 'utils/errors/normalize'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'
import { clearManualLogout, clearSsoRedirectTried, rememberAuthMethod } from 'utils/authStorage'

type MicrosoftTokenGetAvailableCompanies = {
  authMicrosoftTokenGetAvailableCompanies: IAuthMicrosoftTokenGetAvailableCompaniesResponse
}

type ExchangeMicrosoftToken = {
  authExchangeMicrosoftToken: IAuthUserWithCredentialsResponse
}

export const useMicrosoftLogin = () => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { instance } = useMsal()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [microsoftSignInMt] = useMutation<ExchangeMicrosoftToken>(MICROSOFT_SIGN_IN_MUTATION, {
    context: { handledLocally: true }
  })
  const [microsoftGetCompanies] = useMutation<MicrosoftTokenGetAvailableCompanies>(MICROSOFT_GET_COMPANIES, {
    context: { handledLocally: true }
  })

  const { showModal } = useShowModal({ modalType: Modals.AUTH_COMPANY_SELECT_MODAL })

  // Guards against a double completeLogin when both the MSAL event callback and
  // the silent-SSO attempt resolve for the same session.
  const completingRef = React.useRef(false)

  // Set when the user opts out of the silent attempt (chose password login) —
  // stops us from yanking them back in if ssoSilent resolves afterwards.
  const silentAbortedRef = React.useRef(false)

  /**
   * Exchange a Microsoft id token for a Skefto session: resolve the company
   * (picker if there's more than one), run the exchange mutation, store the
   * session and navigate. Resolves to whether the user ended up logged in.
   */
  const completeLogin = async (idToken: string): Promise<boolean> => {
    if (completingRef.current) {
      return false
    }

    completingRef.current = true

    try {
      enqueueSnackbar('Logging into your account', {
        key: 'login',
        variant: 'progress',
        autoHideDuration: null
      })

      const { data: companiesData } = await microsoftGetCompanies({
        variables: {
          token: idToken
        }
      })

      const companies = companiesData.authMicrosoftTokenGetAvailableCompanies.data || []

      if (!companies.length) {
        enqueueSnackbar('User or company details not found, please contact your admin.', {
          variant: 'error',
          persist: true,
          action: (key) => getSnackbarCloseButton(key, closeSnackbar)
        })

        return false
      }

      let companyId = companies[0].id

      if (companies.length > 1) {
        companyId = await new Promise((resolve) => {
          showModal({ resolve, companies })
        })

        // User closed the picker without selecting a company.
        if (!companyId) {
          return false
        }
      }

      const {
        data: {
          authExchangeMicrosoftToken: { data }
        }
      } = await microsoftSignInMt({
        variables: { token: idToken, companyId }
      })

      dispatch(setCurrentUser(data.user))
      setAccessToken(data.accessToken)
      rememberAuthMethod('microsoft')
      clearManualLogout()
      clearSsoRedirectTried()

      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()

        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }

      return true
    } catch (errors) {
      handleError(errors, 'useMicrosoftLogin.completeLogin')

      enqueueSnackbar(getUserMessage(normalizeError(errors)), {
        variant: 'error',
        persist: true,
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })

      return false
    } finally {
      closeSnackbar('login')
      completingRef.current = false
    }
  }

  const microsoftSignIn = async (request: RedirectRequest = loginRequest) => {
    silentAbortedRef.current = false
    clearManualLogout()

    instance.loginRedirect(request).catch((e) => {
      handleError(e, 'microsoftSignIn.loginRedirect')
    })
  }

  /** Called when the user chooses password login while the silent attempt is running. */
  const abortSilentSignIn = () => {
    silentAbortedRef.current = true
  }

  /**
   * No-UI SSO attempt for the login page: if the browser already has a Microsoft
   * session, MSAL gets a token via a hidden iframe and we exchange it right away.
   * Resolves to whether the user ended up signed in; never throws. When silent
   * auth isn't possible (`InteractionRequiredAuthError`) the caller shows the form.
   *
   * Call `abortSilentSignIn()` (e.g. the user chose password login) to bail out
   * before the token exchange starts.
   */
  const trySilentSignIn = async (): Promise<boolean> => {
    silentAbortedRef.current = false

    // A login is already being completed (e.g. from the redirect event callback).
    if (completingRef.current) {
      return true
    }

    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0]
    const request = account ? { ...silentRequest, account } : silentRequest

    try {
      const result = await instance.ssoSilent(request)

      if (silentAbortedRef.current) {
        return false
      }

      const completed = await completeLogin(result.idToken)

      // If the MSAL event callback got to completeLogin() first, our call was a
      // no-op — a login is still in flight, so keep the caller waiting.
      return completed || completingRef.current
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        console.warn('[useMicrosoftLogin.trySilentSignIn]', error)
      }

      return false
    }
  }

  const onLoginSuccess = async (event: EventMessage) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload
    ) {
      // User bailed to password login while ssoSilent was still resolving —
      // don't sign them back in behind their back.
      if (silentAbortedRef.current && event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        return
      }

      const result = event.payload as AuthenticationResult

      await completeLogin(result?.idToken)
    }
  }

  React.useEffect(() => {
    const callbackId = instance.addEventCallback(onLoginSuccess)

    return () => {
      instance.removeEventCallback(callbackId)
    }
  }, [])

  return {
    microsoftSignIn,
    trySilentSignIn,
    abortSilentSignIn,
    /** True while a login is being completed (e.g. after returning from a redirect). */
    isCompleting: () => completingRef.current
  }
}
